/**
 * URoute shared constants.
 * Consumed by apps/api, apps/web, apps/pwa — treat as a stable contract.
 */
export const ROLES = ['ADMIN', 'DRIVER', 'FINANCE', 'INTEGRATION'];
export const ROLE_LABELS = {
    ADMIN: 'Administrator',
    DRIVER: 'Driver',
    FINANCE: 'Finance',
    INTEGRATION: 'Integration service',
};
/** Canonical job status ids (URL/DB friendly). Display labels below. */
export const JOB_STATUSES = [
    'received',
    'unallocated',
    'allocated',
    'accepted',
    'in_progress',
    'exception',
    'completed',
    'invoice_draft',
    'invoice_sent',
    'payment_completed',
    'rejected',
    'cancelled',
    'void',
];
export const JOB_STATUS_LABELS = {
    received: 'Received',
    unallocated: 'Unallocated',
    allocated: 'Allocated',
    accepted: 'Accepted',
    in_progress: 'In Progress',
    exception: 'Exception',
    completed: 'Completed',
    invoice_draft: 'Invoice Draft',
    invoice_sent: 'Invoice Sent',
    payment_completed: 'Payment Completed',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
    void: 'Void',
};
/** Dashboard ordering of the operational lifecycle (left-to-right pipeline). */
export const JOB_STATUS_ORDER = [
    'received',
    'unallocated',
    'allocated',
    'accepted',
    'in_progress',
    'exception',
    'completed',
    'invoice_draft',
    'invoice_sent',
    'payment_completed',
    'rejected',
    'cancelled',
    'void',
];
/** Statuses a DRIVER may see on the PWA (PRD: driver view). */
export const DRIVER_VISIBLE_STATUSES = [
    'allocated',
    'accepted',
    'in_progress',
    'exception',
    'completed',
];
/** Financial statuses are admin/finance only (never surfaced to drivers). */
export const FINANCIAL_STATUSES = [
    'completed',
    'invoice_draft',
    'invoice_sent',
    'payment_completed',
    'void',
];
export function isDriverVisibleStatus(status) {
    return DRIVER_VISIBLE_STATUSES.includes(status);
}
export function isFinancialStatus(status) {
    return FINANCIAL_STATUSES.includes(status);
}
export const JOB_PRIORITIES = ['low', 'normal', 'high', 'urgent'];
export const JOB_PRIORITY_LABELS = {
    low: 'Low',
    normal: 'Normal',
    high: 'High',
    urgent: 'Urgent',
};
/** Lower rank = more important. */
export const PRIORITY_RANK = {
    urgent: 0,
    high: 1,
    normal: 2,
    low: 3,
};
export const SERVICE_TYPES = ['standard', 'express', 'same_day', 'next_day'];
export const SERVICE_TYPE_LABELS = {
    standard: 'Standard',
    express: 'Express',
    same_day: 'Same Day',
    next_day: 'Next Day',
};
export const JOB_SOURCES = ['csv', 'api', 'manual', 'system'];
export const WEIGHT_UNITS = ['kg', 'lb'];
export const LB_TO_KG = 0.45359237;
export function toKg(value, unit) {
    return unit === 'lb' ? Math.round(value * LB_TO_KG * 100) / 100 : value;
}
export const VEHICLE_TYPES = ['van', 'lorry', 'car', 'motorbike', 'trailer'];
export const DRIVER_STATUSES = ['active', 'inactive', 'on_leave'];
export const VEHICLE_STATUSES = ['active', 'maintenance', 'retired'];
export const INVOICE_STATUSES = [
    'draft',
    'sent',
    'payment_completed',
    'void',
    'credit_required',
];
export const INVOICE_STATUS_LABELS = {
    draft: 'Draft',
    sent: 'Sent',
    payment_completed: 'Payment Completed',
    void: 'Void',
    credit_required: 'Credit Required',
};
export const PRICE_RULE_TYPES = ['fixed', 'per_kg'];
export const PRICE_RULE_STATUSES = ['draft', 'active', 'archived'];
export const PROOF_TYPES = ['photo', 'document', 'signature'];
export const NOTIFICATION_CHANNELS = ['in_app', 'email'];
export const DEFAULT_CURRENCY = 'GBP';
export const DEFAULT_TAX_RATE_PCT = 20.0;
/** Route/API path constants — single source for docs/api.md and the apps. */
export const API_PREFIX = '/api';
export const API_V1_PREFIX = '/api/v1';
export const API_PATHS = {
    health: '/api/health',
    auth: {
        login: `${API_V1_PREFIX}/auth/login`,
        refresh: `${API_V1_PREFIX}/auth/refresh`,
        logout: `${API_V1_PREFIX}/auth/logout`,
        me: `${API_V1_PREFIX}/auth/me`,
    },
    jobs: `${API_V1_PREFIX}/jobs`,
    intake: {
        jobs: `${API_V1_PREFIX}/intake/jobs`,
        csv: `${API_V1_PREFIX}/intake/csv`,
    },
    allocation: `${API_V1_PREFIX}/jobs/allocation`,
    status: `${API_V1_PREFIX}/jobs/status`,
    proofs: `${API_V1_PREFIX}/proofs`,
    drivers: `${API_V1_PREFIX}/drivers`,
    vehicles: `${API_V1_PREFIX}/vehicles`,
    clients: `${API_V1_PREFIX}/clients`,
    zones: `${API_V1_PREFIX}/zones`,
    routes: `${API_V1_PREFIX}/routes`,
    pricing: `${API_V1_PREFIX}/pricing/rules`,
    invoices: `${API_V1_PREFIX}/invoices`,
    payments: `${API_V1_PREFIX}/payments`,
    analytics: `${API_V1_PREFIX}/analytics`,
    notifications: `${API_V1_PREFIX}/notifications`,
    audit: `${API_V1_PREFIX}/audit`,
    settings: `${API_V1_PREFIX}/settings`,
    users: `${API_V1_PREFIX}/users`,
};
/** Dev server ports (see AGENTS.md / docs/ARCHITECTURE.md). */
export const PORTS = {
    api: 4410,
    web: 4420,
    pwa: 4430,
    e2e: 4440,
};
export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 200;
//# sourceMappingURL=constants.js.map